/**
 * Helpers for the two GraphQL type names an AA sequence can arrive under (BNCH-169122).
 *
 * `ProteinNode` is being replaced by `AaSequenceNode`. Both exist in the schema during the
 * migration, and which one the server emits as `__typename` changes at the canonical flip, so
 * client code has to accept either.
 *
 * Where the two branches select identical fields, codegen collapses them into a single generated
 * type whose `__typename` is the union `"AaSequenceNode" | "ProteinNode"`. TypeScript will not
 * narrow such a branch away from an equality check on one of its members, so comparing
 * `__typename` directly stops discriminating the surrounding union. The type predicates here
 * narrow correctly in both directions and read better than a two-term comparison.
 *
 * Delete this module once `ProteinNode` is removed and only one name remains.
 */

export const AA_SEQUENCE_NODE_TYPENAMES = ['ProteinNode', 'AaSequenceNode'] as const;
export const AA_SEQUENCE_VERSION_NODE_TYPENAMES = ['ProteinVersionNode', 'AaSequenceVersionNode'] as const;

type AaSequenceNodeTypename = (typeof AA_SEQUENCE_NODE_TYPENAMES)[number];
type AaSequenceVersionNodeTypename = (typeof AA_SEQUENCE_VERSION_NODE_TYPENAMES)[number];

/** Narrow a GraphQL union member to the AA sequence entity branch, under either type name. */
export function isAaSequenceNode<T extends {__typename: string}>(
  node: T
): node is Extract<T, {__typename: AaSequenceNodeTypename}> {
  return (AA_SEQUENCE_NODE_TYPENAMES as ReadonlyArray<string>).includes(node.__typename);
}

/** Narrow a GraphQL union member to the AA sequence version branch, under either type name. */
export function isAaSequenceVersionNode<T extends {__typename: string}>(
  node: T
): node is Extract<T, {__typename: AaSequenceVersionNodeTypename}> {
  return (AA_SEQUENCE_VERSION_NODE_TYPENAMES as ReadonlyArray<string>).includes(node.__typename);
}
